import React, { useState, useEffect } from "react";

const FileUploadComponent = ({
  name,
  label,
  handleUploade,
  isDisabled,
  className,
  isMandatory,
  Originaldocuments,
  isMultiple,
  showAddButton,
  files,
  onRemove,
}) => {
  // Fallback display state (used only when `files` is not provided)
  const [documents, setDocuments] = useState({});
  const [inputCount, setInputCount] = useState(1);

  // When the parent passes the actual uploaded documents array, render the
  // improved removable list instead of the legacy per-input "Selected file".
  const hasList = Array.isArray(files);

  let pointer = isDisabled ? "cursor-not-allowed" : "cursor-pointer";
  let hover =
    className?.toString?.() === "true" ? "bg-red-500" : "bg-slate-500";

  const handleUpload = (e, index) => {
    handleUploade?.(e);

    if (hasList) return; // list is driven by the parent `files` prop

    const selected = Array.from(e?.target?.files || []);
    if (selected.length > 0) {
      const fileNames = selected.map((file) => file?.name || "").join(", ");
      if (fileNames.length > 50) {
        setDocuments(fileNames.substring(0, 50) + "...");
      } else {
        setDocuments((prev) => ({
          ...prev,
          [index]: fileNames,
        }));
      }
    }
  };

  useEffect(() => {
    if (hasList) return; // not needed in list mode

    if (Originaldocuments?.toString?.().trim?.() !== "") {
      const formattedDocuments = {};

      if (Array.isArray(Originaldocuments)) {
        Originaldocuments.forEach((file, index) => {
          formattedDocuments[index] =
            file?.name?.length > 20
              ? file.name.substring(0, 20) + "..."
              : file?.name || "";
        });
      } else {
        formattedDocuments[0] =
          Originaldocuments?.length > 20
            ? Originaldocuments.substring(0, 20) + "..."
            : Originaldocuments;
      }

      setDocuments(formattedDocuments);
    }

    return () => {
      setDocuments({});
    };
  }, [Originaldocuments, hasList]);

  const hasUploaded = hasList && files.length > 0;

  return (
    <div className="flex w-full items-start mb-3">
      <div className="w-[220px] text-right pr-4 text-sm font-medium text-gray-700 pt-2">
        {label}
        {isMandatory && <span className="text-red-500 text-xl">*</span>}
      </div>

      <div className="flex flex-col gap-2 min-w-[260px]">
        {/* Improved list of all uploaded documents with per-file removal */}
        {hasUploaded && (
          <div className="flex flex-col gap-1.5">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-slate-100 rounded-md px-3 py-1.5 w-[260px]"
              >
                <span className="text-sm text-gray-700 truncate mr-2">
                  {file?.name || file?.fileName || "Document"}
                </span>
                {onRemove && !isDisabled && (
                  <button
                    type="button"
                    aria-label="Remove document"
                    title="Remove"
                    onClick={() => onRemove(index)}
                    className="text-red-500 hover:text-red-600 font-bold text-lg leading-none px-1"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload control */}
        {hasList ? (
          <div className="flex items-center gap-2 relative">
            <input
              id={`${name}-upload`}
              name={name}
              type="file"
              multiple={isMultiple}
              onChange={(e) => handleUpload(e, 0)}
              disabled={isDisabled}
              className={`absolute inset-0 opacity-0 ${pointer}`}
            />
            <label
              htmlFor={`${name}-upload`}
              className={`block text-white py-2 px-4 rounded-md border ${hover} ${pointer} transition-colors duration-300 ease-in-out ${className || ""}`}
            >
              {hasUploaded ? "+ Add file" : "Upload file"}
            </label>
          </div>
        ) : (
          // Legacy fallback (used where `files` is not provided, e.g. edit page)
          [...Array(inputCount)].map((_, index) => (
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 relative">
                <input
                  id={`${name}-${index}`}
                  name={name}
                  type="file"
                  multiple={isMultiple}
                  onChange={(e) => handleUpload(e, index)}
                  disabled={isDisabled}
                  className={`absolute inset-0 opacity-0 ${hover}`}
                />

                <label
                  htmlFor={`${name}-${index}`}
                  className={`block text-white py-2 px-4 rounded-md border ${hover} ${pointer} transition-colors duration-300 ease-in-out ${className || ""}`}
                >
                  Upload file
                </label>

                {showAddButton && index === inputCount - 1 && (
                  <button
                    type="button"
                    onClick={() => setInputCount((prev) => prev + 1)}
                    className="bg-blue-600 text-white w-8 h-8 rounded-full text-lg font-bold"
                  >
                    +
                  </button>
                )}
              </div>

              {documents[index] && (
                <p className="text-sm text-gray-600 ml-1">
                  Selected file: {documents[index]}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FileUploadComponent;
